import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Paper,
  CircularProgress,
  TextField,
  Alert,
  Stack
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import errorHandler from './utils/errorHandler';

// ── Inline SVG logo mark — no file dependency, always crisp ──
const LogoMark = ({ size = 120, white = false }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }}
  >
    <circle cx="100" cy="100" r="97" fill={white ? 'rgba(255,255,255,0.15)' : '#2f3542'} />
    <circle cx="100" cy="100" r="89" fill="none" stroke="white" strokeWidth="1.5" opacity="0.18" />
    <path d="M 52,68 A 62,62 0 0,1 158,85" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.35" />
    <path d="M 148,132 A 62,62 0 0,1 42,115" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.35" />
    <text x="100" y="140" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontWeight="800" fontSize="115" textAnchor="middle" fill="white">S</text>
  </svg>
);

const LandingPage = () => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showLanding, setShowLanding] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const { login, isLoggingIn } = useAuth();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        errorHandler.logDebug('LandingPage', 'Starting authentication check');

        const localUser = localStorage.getItem('user');
        const localToken = localStorage.getItem('access_token');

        if (localUser && localToken) {
          try {
            const userData = JSON.parse(localUser);
            if (userData.id && userData.email) {
              if (userData.subscription_tier === 'solo') {
                navigate('/solo-dashboard');
              } else {
                navigate('/dashboard');
              }
              return;
            }
          } catch (parseError) {
            localStorage.removeItem('user');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
          }
        }

        const headers = { 'Content-Type': 'application/json' };
        if (localToken) headers['Authorization'] = `Bearer ${localToken}`;

        const sessionResponse = await fetch('/api/auth/check-session', {
          method: 'GET',
          credentials: 'include',
          headers
        });

        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          if (sessionData.success && sessionData.user_id) {
            try {
              const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
              if (storedUser.subscription_tier === 'solo') {
                navigate('/solo-dashboard');
              } else {
                navigate('/dashboard');
              }
            } catch {
              navigate('/dashboard');
            }
            return;
          }
        }

        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const authState = urlParams.get('state');

        if (authCode && authState === 'auth0_sso') {
          try {
            window.location.href = `/api/auth/sso-entry?code=${authCode}&state=${authState}`;
            return;
          } catch (ssoError) {
            errorHandler.logError('LandingPage', 'SSO processing failed', ssoError);
          }
        }

        setShowLanding(true);
        setIsCheckingAuth(false);

      } catch (error) {
        errorHandler.logError('LandingPage', 'Authentication check failed', error);
        setShowLanding(true);
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;
    setLoginError('');

    try {
      const cleanEmail = loginData.email.trim().toLowerCase();
      const result = await login(cleanEmail, loginData.password);

      if (result.success) {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = storedUser.role;
        const userTier = storedUser.subscription_tier;

        if (userRole === 'RENTAL_USER') {
          navigate('/rental');
        } else if (userRole === 'SOLO' || userTier === 'solo') {
          navigate('/solo-dashboard');
        } else if (userRole === 'ACCOUNT_MANAGER') {
          navigate('/manager_dashboard');
        } else if (userRole === 'ACCOUNT_USER') {
          navigate('/employeeportal');
        } else {
          navigate('/dashboard');
        }
      } else {
        setLoginError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      errorHandler.logError('LandingPage', 'Login error', error);
      setLoginError('An error occurred during login. Please try again.');
    }
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setLoginError('');
  };

  if (isCheckingAuth) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="textSecondary">Salon Logic</Typography>
          <Typography variant="body2" color="textSecondary">Checking authentication...</Typography>
        </Box>
      </Container>
    );
  }

  if (!showLanding) return null;

  return (
    <Container maxWidth="lg">

      {/* Hero */}
      <Box sx={{ my: 6, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <LogoMark size={100} />
        </Box>
        <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          Salon Logic
        </Typography>
        <Typography variant="h5" color="textSecondary" sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
          Built on 20 years of real industry experience. No commission, no booking fees —
          just honest software that works.
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: 6 }}>

        {/* Login Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={4} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LockOutlinedIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>Login</Typography>
              </Box>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Access your Salon Logic dashboard to manage operations, view reports, and more.
              </Typography>

              {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}

              <form onSubmit={handleLoginSubmit}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth label="Email Address" name="email" type="email"
                    value={loginData.email} onChange={handleLoginChange}
                    required autoComplete="email"
                  />
                  <TextField
                    fullWidth label="Password" name="password" type="password"
                    value={loginData.password} onChange={handleLoginChange}
                    required autoComplete="current-password"
                  />
                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    disabled={isLoggingIn} sx={{ mt: 2, py: 1.5 }}
                  >
                    {isLoggingIn ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                  </Button>
                </Stack>
              </form>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link to="/password-recovery" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                    Forgot your password?
                  </Typography>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Register Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={4} sx={{ height: '100%', bgcolor: 'primary.main', color: 'white', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ mr: 1.5, flexShrink: 0 }}>
                  <LogoMark size={32} white />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  New to Salon Logic?
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                Join UK hair and beauty professionals running their business on honest,
                flat-price software — built by someone who's actually been in the industry.
              </Typography>

              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    {[
                      '115+ features across all plans',
                      'Complete financial management',
                      'Staff, HR & compliance',
                      'VAT & MTD ready',
                    ].map(text => (
                      <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    {[
                      'Payroll export (Sage/Xero)',
                      'Time clock & attendance',
                      'UK employment law',
                      'From £29.99/month',
                    ].map(text => (
                      <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>

              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button
                  component={Link} to="/register"
                  variant="contained" size="large" fullWidth
                  sx={{ py: 1.5, bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Create Your Account
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: 'grey.50' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
          Everything you need to run your salon
        </Typography>
        <Grid container spacing={3}>
          {[
            { title: 'Financial management', body: 'Track income, expenses, VAT, and generate comprehensive reports' },
            { title: 'Staff management', body: 'Employee profiles, reviews, payroll, and performance tracking' },
            { title: 'Time & attendance', body: 'Holiday management, absence tracking, and shift scheduling' },
            { title: 'Business intelligence', body: 'Forecasting, analytics, and data-driven decision making' },
          ].map(item => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{item.title}</Typography>
                <Typography variant="body2" color="textSecondary">{item.body}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Pricing */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Simple, transparent pricing
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>SL Solo</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Self-employed · no staff
                </Typography>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 700, mb: 1 }}>£29.99</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>per month · cancel anytime</Typography>
                <Typography variant="body2">
                  Bookings, clients, money, and compliance — built for working alone
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={4} sx={{ border: 2, borderColor: 'primary.main' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">SL Pro</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Salon with a team
                </Typography>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 700, mb: 1 }}>£119.99</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>per month · cancel anytime</Typography>
                <Typography variant="body2">
                  Everything in Solo, plus multi-employee scheduling, payroll, HR and compliance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 4, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="textSecondary">
          © 2026 Salon Logic Ltd · Company No. SC885704 · Registered in Scotland
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Need help?{' '}
          <a href="mailto:support@salonlogic.co.uk" style={{ color: 'inherit' }}>
            support@salonlogic.co.uk
          </a>
        </Typography>
      </Box>

    </Container>
  );
};

export default LandingPage;